// src/components/LandingPage.js — calm, codex-vibe landing.
//
// The chat is the hero. The user's first message is the demo. Everything
// else is supporting tissue — kept minimal so the page reads in seconds.
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';

import { useTutor } from '../contexts/TutorRuntimeContext';
import { Button } from './ui';
import { MessageSquare } from 'lucide-react';

const PRINCIPLES = [
  {
    title: 'Tell it what you want',
    body: 'A chat assistant figures out the right course for you. No category trees, no checkboxes.',
  },
  {
    title: 'Practice that adapts',
    body: 'Questions react to what you actually know — generated fresh each session, never recycled.',
  },
  {
    title: 'Memory across sessions',
    body: 'It remembers what you struggled with last time and nudges you back to it when you return.',
  },
];

export default function LandingPage() {
  const { panelOpen, openPanel } = useTutor();
  return (
    <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
      {/* Hero — the docked Tutor panel on the right IS the chat. The
          landing's job is just to point the user at it. */}
      <header className="mb-12">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-ink leading-tight">
          Learn anything,<br className="hidden sm:block" /> the way you learn.
        </h1>
        <p className="mt-4 text-base text-mutedink max-w-xl">
          An AI tutor that builds your course around what you actually want to
          know. Tell it your goal — it takes care of the rest.
        </p>
      </header>

      {!panelOpen && (
        <div className="mb-20">
          <Button variant="primary" onClick={openPanel}>
            <MessageSquare className="w-4 h-4" />
            Open the tutor
          </Button>
        </div>
      )}

      {/* Three calm rows — no big numbered circles, no bg-[var(--code-block-bg)] cards */}
      <section className="mb-20">
        <h2 className="text-sm font-medium text-mutedink uppercase tracking-wider mb-6">
          How it works
        </h2>
        <div className="space-y-6">
          {PRINCIPLES.map((p) => (
            <div key={p.title} className="flex gap-4">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
              <div>
                <h3 className="text-base font-medium text-ink">{p.title}</h3>
                <p className="text-sm text-mutedink leading-relaxed mt-1">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quiet footer — sign-in entry for returning users */}
      <footer className="border-t border-line pt-8 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-mutedink">
          Already have an account?
        </p>
        <Link
          to="/my-courses"
          className="inline-flex items-center gap-1.5 text-sm text-accent hover:text-accent-hover font-medium transition"
        >
          <BookOpen className="w-4 h-4" />
          Continue where you left off
          <ArrowRight className="w-4 h-4" />
        </Link>
      </footer>
    </div>
  );
}
